<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { del, get, post, put } from '@/api'

definePage({
  meta: {
    title: '회의실 예약',
    action: 'read',
    subject: 'meeting-room',
  },
})

// 회의 모델 인터페이스
interface Conference {
  id: number | null
  target_date: string | null
  room_type: string | null
  title: string | null
  start_at: string | null
  duration: number
}

// 초기 예약 모델
const getInitialReservation = (): Conference => ({
  id: null,
  target_date: null,
  room_type: null,
  title: null,
  start_at: null,
  duration: 30,
})

// 상태
const selectedDate = ref(new Date().toISOString().split('T')[0])
const reservation = ref<Conference>(getInitialReservation())
const conferences = ref<Conference[]>([])
const selectedCell = ref<string | null>(null)
const loading = ref(false)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 회의실 타입
const roomTypes = [
  { value: null, title: '회의실을 선택해주세요' },
  { value: 'the-boss-room', title: 'THE BOSS ROOM' },
  { value: 'the-five-oceans', title: 'THE FIVE OCEANS' },
  { value: 'the-six-continents', title: 'THE SIX CONTINENTS' },
]

// 시간 옵션 생성
const timeOptions = computed(() => {
  const options = []
  for (let i = 6; i <= 21; i++) {
    const prefix = i < 10 ? '0' : ''

    options.push({ value: `${prefix}${i}:00`, title: `${prefix}${i}:00` })
    options.push({ value: `${prefix}${i}:30`, title: `${prefix}${i}:30` })
  }

  return options
})

// 시간 인덱스 변환 함수
const timeFrom = (index: number): string => {
  const prefix = index < 20 ? '0' : ''
  const postfix = index % 2 ? '30' : '00'

  return `${prefix}${Math.floor(index / 2)}:${postfix}`
}

const indexFrom = (time: string): number => {
  const timeArr = time.split(':')
  let index = Number.parseInt(timeArr[0], 10) * 2
  if (timeArr[1] === '30')
    index += 1

  return index
}

// 그리드 데이터 맵
const rowspanMap = reactive<Record<string, number>>({})
const displayMap = reactive<Record<string, string>>({})
const conferenceModels = reactive<Record<string, Conference>>({})

// 그리드 초기화
const initializeGrid = () => {
  const baseModel = getInitialReservation()

  baseModel.target_date = selectedDate.value

  for (let i = 0; i < 48; i++) {
    const rooms = ['the-boss-room', 'the-five-oceans', 'the-six-continents']

    rooms.forEach(room => {
      const key = `${room}-${i}`

      rowspanMap[key] = 1
      displayMap[key] = 'table-cell'
      conferenceModels[key] = {
        ...getInitialReservation(),
        target_date: selectedDate.value,
        start_at: timeFrom(i),
        room_type: room,
      }
    })
  }
}

// 데이터 로드
const loadConferences = async () => {
  loading.value = true
  initializeGrid()

  try {
    const { success, data } = await get<Conference[]>(`/conference?target_date=${selectedDate.value}`)

    if (success && data) {
      conferences.value = data

      data.forEach(conference => {
        if (!conference.start_at || !conference.room_type)
          return

        const startIndex = indexFrom(conference.start_at)
        const cell = `${conference.room_type}-${startIndex}`
        const span = conference.duration / 30

        rowspanMap[cell] = span

        for (let idx = startIndex + 1; idx < startIndex + span; idx++) {
          const targetCell = `${conference.room_type}-${idx}`

          displayMap[targetCell] = 'none'
        }

        conferenceModels[cell] = conference
      })
    }
  }
  catch (error) {
    console.error('Failed to load conferences:', error)
  }
  finally {
    loading.value = false
  }
}

// 날짜 이동
const shiftDate = (days: number) => {
  const date = new Date(selectedDate.value)

  date.setDate(date.getDate() + days)
  selectedDate.value = date.toISOString().split('T')[0]
}

// 셀 클릭
const selectCell = (cell: string) => {
  selectedCell.value = cell
  reservation.value = { ...conferenceModels[cell] }
}

// 셀 스타일
const getCellClass = (room: string, index: number) => {
  const cell = `${room}-${index}`
  const classes: string[] = []

  if (selectedCell.value === cell)
    classes.push(`selected-${room}`)

  if (conferenceModels[cell]?.title)
    classes.push(`booked-${room}`)

  return classes
}

// 예약 등록
const store = async () => {
  if (!reservation.value.title || !reservation.value.room_type) {
    showNotification('회의 명칭과 회의실을 선택해주세요.', 'warning')

    return
  }

  try {
    const payload = {
      ...reservation.value,
      target_date: selectedDate.value,
    }

    const { success, error } = await post('/conference', payload)

    if (success) {
      showNotification('예약이 등록되었습니다.')
      await loadConferences()
      clearReservation()
    }
    else {
      showNotification(error?.response?.data?.errorMessage || '등록에 실패했습니다.', 'error')
    }
  }
  catch (error: any) {
    showNotification(error?.response?.data?.errorMessage || '등록에 실패했습니다.', 'error')
  }
}

// 예약 수정
const update = async () => {
  if (!reservation.value.id)
    return

  try {
    const { success, error } = await put(`/conference/${reservation.value.id}`, reservation.value)

    if (success) {
      showNotification('예약이 수정되었습니다.')
      await loadConferences()
      clearReservation()
    }
    else {
      showNotification(error?.response?.data?.errorMessage || '수정에 실패했습니다.', 'error')
    }
  }
  catch (error: any) {
    showNotification(error?.response?.data?.errorMessage || '수정에 실패했습니다.', 'error')
  }
}

// 예약 삭제
const remove = async () => {
  if (!reservation.value.id)
    return

  try {
    const { success } = await del(`/conference/${reservation.value.id}`)

    if (success) {
      showNotification('예약이 삭제되었습니다.')
      await loadConferences()
      clearReservation()
    }
    else {
      showNotification('삭제에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    showNotification('삭제에 실패했습니다.', 'error')
  }
}

// 초기화
const clearReservation = () => {
  reservation.value = getInitialReservation()
  selectedCell.value = null
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 날짜 포맷
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  }

  return date.toLocaleDateString('ko-KR', options)
}

// 시간 범위 (06:00 ~ 22:00)
const timeSlots = computed(() => {
  const slots = []
  for (let i = 12; i <= 44; i++)
    slots.push(i)

  return slots
})

// 감시자
watch(selectedDate, () => {
  loadConferences()
})

onMounted(() => {
  initializeGrid()
  loadConferences()
})
</script>

<template>
  <VRow>
    <!-- 회의실 예약 현황 -->
    <VCol
      cols="12"
      md="8"
    >
      <VCard>
        <VCardTitle>회의실 예약 현황</VCardTitle>
        <VCardText>
          <!-- 날짜 선택 -->
          <VRow
            align="center"
            class="mb-4"
          >
            <VCol cols="auto">
              <VBtn
                icon="tabler-chevron-left"
                variant="text"
                @click="shiftDate(-1)"
              />
            </VCol>
            <VCol>
              <VTextField
                v-model="selectedDate"
                type="date"
                density="compact"
                hide-details
                class="text-center"
              />
              <div class="text-center text-subtitle-2 mt-1">
                {{ formatDate(selectedDate) }}
              </div>
            </VCol>
            <VCol cols="auto">
              <VBtn
                icon="tabler-chevron-right"
                variant="text"
                @click="shiftDate(1)"
              />
            </VCol>
          </VRow>

          <!-- 예약 그리드 -->
          <VTable
            density="compact"
            class="reservation-grid"
          >
            <thead>
              <tr>
                <th class="text-center">
                  시간
                </th>
                <th class="text-center room-boss">
                  The Boss Room
                </th>
                <th class="text-center room-oceans">
                  The Five Oceans
                </th>
                <th class="text-center room-continents">
                  The Six Continents
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="index in timeSlots"
                :key="index"
              >
                <td class="text-center time-cell">
                  {{ timeFrom(index) }}
                </td>
                <td
                  v-if="displayMap[`the-boss-room-${index}`] !== 'none'"
                  :rowspan="rowspanMap[`the-boss-room-${index}`]"
                  class="room-cell"
                  :class="[...getCellClass('the-boss-room', index)]"
                  @click="selectCell(`the-boss-room-${index}`)"
                >
                  {{ conferenceModels[`the-boss-room-${index}`]?.title || '' }}
                </td>
                <td
                  v-if="displayMap[`the-five-oceans-${index}`] !== 'none'"
                  :rowspan="rowspanMap[`the-five-oceans-${index}`]"
                  class="room-cell"
                  :class="[...getCellClass('the-five-oceans', index)]"
                  @click="selectCell(`the-five-oceans-${index}`)"
                >
                  {{ conferenceModels[`the-five-oceans-${index}`]?.title || '' }}
                </td>
                <td
                  v-if="displayMap[`the-six-continents-${index}`] !== 'none'"
                  :rowspan="rowspanMap[`the-six-continents-${index}`]"
                  class="room-cell"
                  :class="[...getCellClass('the-six-continents', index)]"
                  @click="selectCell(`the-six-continents-${index}`)"
                >
                  {{ conferenceModels[`the-six-continents-${index}`]?.title || '' }}
                </td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 회의실 예약 관리 -->
    <VCol
      cols="12"
      md="4"
    >
      <VCard>
        <VCardTitle>회의실 예약 관리</VCardTitle>
        <VCardText>
          <VForm @submit.prevent>
            <!-- 회의 명칭 -->
            <VTextField
              v-model="reservation.title"
              label="회의 명칭"
              placeholder="회의명칭을 입력하세요"
              class="mb-4"
            />

            <!-- 회의실 -->
            <VSelect
              v-model="reservation.room_type"
              :items="roomTypes"
              item-title="title"
              item-value="value"
              label="회의실"
              disabled
              class="mb-4"
            />

            <!-- 시작 시간 -->
            <VSelect
              v-model="reservation.start_at"
              :items="timeOptions"
              item-title="title"
              item-value="value"
              label="시작 시간"
              disabled
              class="mb-4"
            />

            <!-- 회의 시간 -->
            <div class="mb-4">
              <label class="text-subtitle-2 d-block mb-2">회의 시간</label>
              <VRadioGroup
                v-model="reservation.duration"
                inline
              >
                <VRadio
                  label="30분"
                  :value="30"
                />
                <VRadio
                  label="60분"
                  :value="60"
                />
              </VRadioGroup>
            </div>

            <!-- 버튼 -->
            <div class="d-flex gap-2">
              <template v-if="reservation.id">
                <VBtn
                  color="primary"
                  @click="update"
                >
                  수정하기
                </VBtn>
                <VBtn
                  color="error"
                  @click="remove"
                >
                  삭제하기
                </VBtn>
              </template>
              <template v-else>
                <VBtn
                  color="primary"
                  @click="store"
                >
                  등록하기
                </VBtn>
                <VBtn
                  variant="outlined"
                  @click="clearReservation"
                >
                  초기화
                </VBtn>
              </template>
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VRow>
</template>

<style scoped lang="scss">
.reservation-grid {
  th, td {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  .time-cell {
    background-color: rgba(var(--v-theme-surface-variant), 0.5);
    font-weight: 500;
    min-width: 70px;
  }

  .room-cell {
    cursor: pointer;
    transition: all 0.2s;
    min-width: 120px;
    vertical-align: middle;
    text-align: center;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.1);
    }
  }

  .room-boss {
    background-color: rgba(244, 67, 54, 0.1);
  }

  .room-oceans {
    background-color: rgba(33, 150, 243, 0.1);
  }

  .room-continents {
    background-color: rgba(76, 175, 80, 0.1);
  }

  .selected-the-boss-room {
    box-shadow: inset 0 0 0 2px #f44336 !important;
    background-color: rgba(244, 67, 54, 0.2) !important;
  }

  .selected-the-five-oceans {
    box-shadow: inset 0 0 0 2px #2196f3 !important;
    background-color: rgba(33, 150, 243, 0.2) !important;
  }

  .selected-the-six-continents {
    box-shadow: inset 0 0 0 2px #4caf50 !important;
    background-color: rgba(76, 175, 80, 0.2) !important;
  }

  .booked-the-boss-room {
    background-color: rgba(244, 67, 54, 0.3);
    border-left: 3px solid #f44336;
  }

  .booked-the-five-oceans {
    background-color: rgba(33, 150, 243, 0.3);
    border-left: 3px solid #2196f3;
  }

  .booked-the-six-continents {
    background-color: rgba(76, 175, 80, 0.3);
    border-left: 3px solid #4caf50;
  }
}
</style>
